import { Button, Container, Flex } from '@everlywell/ui-kit';
import StickyFooter from 'components/StickyFooter';
import { useAsyncProgramPPQContext } from 'contexts/AsyncProgramPPQ/AsyncProgramPPQContext';
import React from 'react';
import {
  DeepPartial,
  FieldValues,
  FormProvider,
  SubmitHandler,
  UnpackNestedValue,
  useForm,
} from 'react-hook-form';

import { ASYNC_PROGRAMS_QUIZ_IDS, QuizIdsEnum } from '../constants';
import YouAreEligible, {
  PrePurchaseQuizEmailData,
} from '../EligibilityScreens/YouAreEligible';
import YouAreNotEligible from '../EligibilityScreens/YouAreNotEligible';
import FormTitle from '../FormFields/FormTitle';
import OptionSelectorForm, {
  utiQuestions,
  bvAndYiQuestions,
  ghQuestions,
  csQuestions,
  OptionSelectFormProps,
} from '../FormFields/OptionSelectForm';
import PersonalDataForm, {
  PrePurchaseQuizPersonalData,
} from '../FormFields/PersonalDataForm';
import { isPersonalDataIneligible } from '../FormFields/PersonalDataForm/utils';
import InitialQuizScreen from '../InitialQuizScreen';

interface AsyncProgramPPQModalStepProps<T extends FieldValues> {
  mainHeading?: string;
  secondaryHeading?: string;
  defaultValues: UnpackNestedValue<DeepPartial<T>>;
  onSubmit: SubmitHandler<T>;
  FormComponent: React.ComponentType;
  buttonText?: string;
  showNextButton?: boolean;
}

export function AsyncProgramPPQModalInitialScreen({
  quizId,
}: {
  quizId: QuizIdsEnum;
}) {
  return (
    <Flex backgroundColor="sky.dark" justifyContent="center">
      <Container size={['fluid', null, null, 'xl']}>
        <InitialQuizScreen quizId={quizId} />
      </Container>
    </Flex>
  );
}

/**
 * AsyncProgramPPQModalStep Component
 *
 * This component renders a form step within a pre-purchase quiz modal. It utilizes React Hook Form for form state management.
 *
 * @template T - The type of the form values.
 *
 * @param {AsyncProgramPPQModalStepProps<T>} props - The props for the component.
 * @param {string} props.mainHeading - The main heading text for the form step.
 * @param {string} props.secondaryHeading - The secondary heading text for the form step.
 * @param {T} props.defaultValues - The default values for the form fields.
 * @param {function} props.onSubmit - The function to call when the form is submitted.
 * @param {React.ComponentType} props.FormComponent - The form component to render within the form step.
 *
 * @returns {JSX.Element} The rendered form step component.
 */
function AsyncProgramPPQModalStep<T extends FieldValues>({
  mainHeading,
  secondaryHeading,
  defaultValues,
  onSubmit,
  FormComponent,
  buttonText,
  showNextButton = true,
}: AsyncProgramPPQModalStepProps<T>): JSX.Element {
  const formMethods = useForm<T>({
    defaultValues,
    mode: 'onBlur',
  });

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={formMethods.handleSubmit(onSubmit)} noValidate>
        <FormTitle
          mainHeading={mainHeading || ''}
          secondaryHeading={secondaryHeading || ''}
        />
        <FormComponent />
        {showNextButton && (
          <StickyFooter type="fixed">
            <Container
              size={['sm', 'sm', 'fluid']}
              paddingTop="6"
              paddingBottom={[6, null, 0]}
            >
              <Button width={['100%', null, 'fit-content']} type="submit">
                {buttonText || 'Next'}
              </Button>
            </Container>
          </StickyFooter>
        )}
      </form>
    </FormProvider>
  );
}

export function AsyncProgramPPQModalFirstStep({
  quizId,
}: {
  quizId: QuizIdsEnum;
}): JSX.Element {
  const { nextStep, setPersonalData, personalData, setIsAnswerIneligible } =
    useAsyncProgramPPQContext();

  const handleSubmit: SubmitHandler<PrePurchaseQuizPersonalData> = (
    formData,
  ) => {
    if (isPersonalDataIneligible(formData, quizId)) {
      setIsAnswerIneligible(true);
    }

    setPersonalData(formData);
    nextStep();
  };

  return (
    <AsyncProgramPPQModalStep
      mainHeading="To start..."
      secondaryHeading="Let’s get to know each other"
      defaultValues={personalData || {}}
      onSubmit={handleSubmit}
      FormComponent={PersonalDataForm}
    />
  );
}

export function AsyncProgramPPQModalOptionSelections(
  quizId: QuizIdsEnum,
): JSX.Element[] {
  // Array of option selection steps
  const options: OptionSelectFormProps[] = {
    [ASYNC_PROGRAMS_QUIZ_IDS['uti-async']]: utiQuestions,
    [ASYNC_PROGRAMS_QUIZ_IDS['bv-async']]: bvAndYiQuestions,
    [ASYNC_PROGRAMS_QUIZ_IDS['genital-herpes-async']]: ghQuestions,
    [ASYNC_PROGRAMS_QUIZ_IDS['yeast-infection-async']]: bvAndYiQuestions,
    [ASYNC_PROGRAMS_QUIZ_IDS['cold-sores-async']]: csQuestions,
  }[quizId];

  if (!options) return [];

  return options.map((option, index: number) => (
    <OptionSelectorForm key={`option-select-form-${index}`} {...option} />
  ));
}

export function AsyncProgramPPQModalConfirmationStep({
  quizId,
}: {
  quizId: QuizIdsEnum;
}) {
  const { setEmailData, emailData } = useAsyncProgramPPQContext();

  const handleSubmit: SubmitHandler<PrePurchaseQuizEmailData> = (formData) => {
    setEmailData(formData);
  };

  return (
    <Flex backgroundColor="moonlight.base" justifyContent="center">
      <Container size="fluid">
        <AsyncProgramPPQModalStep
          defaultValues={emailData || {}}
          onSubmit={handleSubmit}
          FormComponent={() => <YouAreEligible quizId={quizId} />}
          showNextButton={false}
        />
      </Container>
    </Flex>
  );
}

export const AsyncProgramPPQModalYouAreNotEligibleStep = () => (
  <Flex backgroundColor="sky.dark" justifyContent="center">
    <Container size={['fluid', null, null, 'xl']}>
      <YouAreNotEligible />
    </Container>
  </Flex>
);
